import React, { ChangeEvent, ReactElement, useCallback, useEffect, useState } from 'react';
import { FormFeedback, Input, InputGroup, InputGroupAddon, Label } from 'reactstrap';
import { concat, replace, uniqueId } from 'lodash';
import { Box } from '@mui/material';
import { Validator, Validators } from '@payer-admin-ui/components-v2/src/inputs/validation';

const boxSx = {
  margin: '.1rem 0',
};

const labelStyle = {
  display: 'block',
  margin: 0,
};

const feedbackStyle = {
  display: 'block',
  margin: 0,
  lineHeight: '1.5em',
  minHeight: '1.5em',
};

export type AmountInputProps = {
  title: string;
  value: string | null | undefined;
  setValue: (value: string | null) => void;
  step?: number;
  placeholder?: string;
  unit?: string;
  validators?: Validator<string>[];
  extraValidators?: Validator<string>[];
  disabled?: boolean;
  onValidationUpdate?: (validationError: null | ReactElement) => void;
};

export const AmountInput = ({
  title,
  value,
  setValue,
  step = 0.01,
  placeholder = '0.00',
  unit,
  validators: baseValidators = [Validators.number, Validators.max2FractionalDigits],
  extraValidators = [],
  disabled = false,
  onValidationUpdate,
}: AmountInputProps) => {
  const [id] = useState(() => uniqueId('AmountInput-'));

  const validators = concat(baseValidators, extraValidators);

  // Run the validation once at init to generate default value here, to get validation going before first onChange call.
  const [validationError, setValidationError] = useState<null | ReactElement>(() =>
    Validators.validate(value, validators),
  );

  useEffect(() => {
    setValidationError(Validators.validate(value, validators));
  }, [value, ...validators]); /*eslint-disable-line react-hooks/exhaustive-deps*/ // can't statically verify correctness

  // Update the encapsulating component with any validation error so that it can react (disable buttons etc)
  useEffect(() => {
    onValidationUpdate && onValidationUpdate(validationError);
  }, [onValidationUpdate, validationError]);

  const handleOnChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      // removing e's here only half works :/
      const newValue = replace(event.target.value, 'e', '');
      setValue && setValue(newValue || null);
    },
    [setValue],
  );

  return (
    <Box sx={boxSx}>
      <Label style={labelStyle}>
        <div>{title}</div>
        <InputGroup>
          <Input
            id={id}
            type="number"
            placeholder={placeholder}
            value={value ?? ''}
            onChange={handleOnChange}
            invalid={!!validationError}
            step={step}
            disabled={disabled}
          />
          {unit && <InputGroupAddon addonType="append">{unit}</InputGroupAddon>}
          <FormFeedback style={feedbackStyle}>{validationError}</FormFeedback>
        </InputGroup>
      </Label>
    </Box>
  );
};
